import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authService } from '@services/api'
import { useAuthStore } from '@store'
import { ROUTES } from '@constants'
import toast from 'react-hot-toast'
import { TextField, PasswordField, SubmitButton } from '@components/auth/AuthFields'

function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [loginMethod, setLoginMethod] = useState('email') // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const switchLoginMethod = (method) => {
    setLoginMethod(method)
    localStorage.setItem('loginMethod', method)
    window.dispatchEvent(new Event('loginMethodChange'))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (loginMethod === 'phone') {
        // Rediriger vers la page de vérification pour connexion par téléphone
        navigate('/phone-verification')
        return
      }

      const loginData = { email: formData.email, password: formData.password }
      const response = await authService.login(loginData)

      // Sauvegarder la préférence "Se souvenir de moi"
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
      } else {
        localStorage.removeItem('rememberMe')
      }

      // Détecter le type: user_type vient du backend; fallback via users_id (admin) vs client_id (client)
      const userType = response.user_type ||
        (response.user?.users_id ? 'admin' : 'client')

      login(response.user, response.token, userType)
      toast.success('Connexion réussie !')

      if (userType === 'admin') {
        localStorage.setItem('admin_token', response.token)
        localStorage.setItem('admin_user', JSON.stringify(response.user))
        navigate('/admin/dashboard')
      } else {
        navigate(ROUTES.LIGDI)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="animate-slide-up">
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">Connexion</h2>
      <p className="mt-2 text-slate-500">Entrez vos informations pour accéder à votre espace.</p>

      {/* Sélecteur de mode de connexion (Email/Téléphone) masqué temporairement.
          Mode email actif par défaut. Ne pas supprimer : décommenter pour réactiver. */}
      {false && (
        <div className="mt-8 inline-flex w-full rounded-md bg-slate-100 p-1">
          {[['email', 'Adresse email'], ['phone', 'Téléphone']].map(([method, label]) => (
            <button
              key={method}
              type="button"
              onClick={() => switchLoginMethod(method)}
              className={`flex-1 rounded-md py-2.5 text-sm font-semibold transition-all ${
                loginMethod === method ? 'bg-white text-[#002A4E] shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        {loginMethod === 'email' ? (
          <TextField
            label="Adresse email"
            icon="mail"
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="vous@exemple.com"
          />
        ) : (
          <TextField
            label="Numéro de téléphone"
            icon="phone"
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="XX XX XX XX XX"
          />
        )}

        <PasswordField
          label="Mot de passe"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
          placeholder="Votre mot de passe"
          aside={
            <Link to={ROUTES.FORGOT_PASSWORD} className="text-sm font-semibold text-[#B8860B] hover:underline underline-offset-2">
              Mot de passe oublié ?
            </Link>
          }
        />

        <label htmlFor="rememberMe" className="flex w-fit cursor-pointer items-center gap-2.5 select-none">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 accent-[#002A4E]"
          />
          <span className="text-sm text-slate-600">Se souvenir de moi</span>
        </label>

        <SubmitButton loading={loading} loadingLabel="Connexion…">
          {loginMethod === 'email' ? 'Se connecter' : 'Recevoir mon code de confirmation'}
        </SubmitButton>
      </form>

      <div className="mt-8 flex items-center gap-4">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Nouveau sur KUDHI ?</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <Link
        to={ROUTES.REGISTER}
        className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-slate-200 bg-white py-3.5 font-semibold text-[#002A4E] transition-colors hover:border-slate-300 hover:bg-slate-50"
      >
        Créer un compte gratuitement
      </Link>
    </div>
  )
}

export default LoginPage
